import { apply, burst, celebrate, onceToday, toast } from '../core/effects';
import { hasGame, S } from '../core/game';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { stamp } from './passport';
import { mrtMenu, order, quiz } from './trips';
import { registerObjects, registerTalk } from './registry';

/** Interactions of Liberty Square / CKS Memorial Hall and Beitou (cks package). */
const tx = () => m().cks;
const shuffle = <T>(a: readonly T[]): T[] => a.map((v) => [Math.random(), v] as const).sort((x, y) => x[0] - y[0]).map((p) => p[1]);
const hour = () => Math.floor(S().time / 60);
const minute = () => S().time % 60;
const hhmm = (h: number) => `${String(h).padStart(2, '0')}:00`;

/** Small learning reward, once a day per key. */
function learned(key: string, d: { stars?: number; study?: number; mood?: number } = { stars: 1, study: 2 }): void {
  if (onceToday(`ck.${key}`)) {
    apply(d);
    toast(tx().learned);
  }
}

/** A landmark fact with an English line spoken aloud, plus extra choices. */
function fact(id: string, icon: string, text: string, en?: string, choices: Choice[] = []): void {
  if (en) sayEnglish(en, 0.85);
  say({ who: objName(id), icon, text: en ? `${text}\n\n🗣️ “${en}”` : text, en, choices: [...choices, { text: m().common.back }] });
}

/** A quiz whose reward is given once a day. */
function ask(id: string, icon: string, intro: string, q: { q: string; a: readonly string[]; right: string; wrong: string }, key = id, after?: () => void): void {
  quiz(objName(id), icon, intro, q.q, q.a, q.right, q.wrong, () => {
    learned(`quiz.${key}`, { stars: 2, study: 3 });
    after?.();
  });
}

/** Pick the polite sentence (shuffled), then run `ok`. */
function polite(who: string, icon: string, prompt: string, options: readonly string[], wrong: string, ok: () => void): void {
  const [good] = options;
  say({
    who,
    icon,
    text: prompt,
    choices: shuffle(options).map((o) => ({
      text: `🗣️ ${o}`,
      run: () => {
        sayEnglish(o, 0.9);
        if (o !== good) {
          sfx('bad');
          return say({ who, icon, text: wrong, choices: [{ text: m().common.back, run: () => polite(who, icon, prompt, options, wrong, ok) }] });
        }
        sfx('ok');
        ok();
      },
    })),
  });
}

// --- Liberty Square --------------------------------------------------------------------------------

function gate(): void {
  const t = tx().gate;
  fact('ck_gate', '📸', t.text, undefined, [
    {
      text: t.photo,
      run: () =>
        polite(npcName('ck_tourist'), '📸', t.how, t.ask, t.wrong, () => {
          sfx('pop');
          burst('sparkle', 10);
          if (onceToday('ck.photo')) apply({ mood: 8, stars: 2 });
          S().forever['ck.gatePhoto'] = true;
          say({ who: objName('ck_gate'), icon: '📸', text: t.done });
        }),
    },
  ]);
}

function steps(): void {
  const t = tx().steps;
  fact('ck_steps', '🧗', t.text, undefined, [
    {
      text: t.climb,
      cost: m().common.minutes(15),
      run: () => {
        if (S().stats.energy < 12) return toast(t.tired);
        sfx('hop');
        sayEnglish(t.countEn, 0.95);
        apply({ energy: -6, mood: 6, minutes: 15 });
        stamp('cks');
        if (onceToday('ck.climb')) apply({ stars: 2 }, { float: false });
        celebrate();
        say({ who: objName('ck_steps'), icon: '🏯', text: t.counting, en: t.countEn });
      },
    },
    { text: t.quiz, run: () => ask('ck_steps', '🔢', t.text, t) },
  ]);
}

/** The ceremony runs on the hour, 9:00–17:00, for the first quarter of an hour. */
export function guardsOn(time = S().time): boolean {
  const h = Math.floor(time / 60), mi = time % 60;
  return h >= 9 && h <= 17 && mi < 15;
}

function guardShow(i: number): void {
  const t = tx().guard;
  const line = t.lines[i];
  if (!line) {
    if (onceToday('ck.guard')) apply({ mood: 10, study: 3, stars: 2 });
    sfx('fanfare');
    return say({ who: objName('ck_guard'), icon: '🎖️', text: t.done });
  }
  sayEnglish(line, 0.85);
  say({ who: objName('ck_guard'), icon: '🎖️', text: `${i === 0 ? `${t.intro}\n\n` : ''}🗣️ “${line}”`, en: line, choices: [{ text: tx().next, run: () => guardShow(i + 1) }] });
}

function guard(): void {
  const t = tx().guard;
  if (guardsOn()) return guardShow(0);
  const h = hour();
  const next = minute() < 15 ? h : h + 1;
  if (next > 17) return say({ who: objName('ck_guard'), icon: '🎖️', text: t.closed });
  say({ who: objName('ck_guard'), icon: '🎖️', text: t.notNow(hhmm(Math.max(9, next))) });
}

function hall(): void {
  const t = tx().hall;
  ask('ck_hall', '🏯', t.text, t, 'ck_hall', () => stamp('cks'));
}

function theater(): void {
  const t = tx().theater;
  fact('ck_theater', '🎭', t.text, undefined, [
    {
      text: t.ask,
      run: () =>
        polite(objName('ck_theater'), '🎫', `“${t.vendor}”`, [t.ok, ...t.bad], m().world3.market.wrong[0], () => {
          learned('theater');
          say({ who: objName('ck_theater'), icon: '🎫', text: t.done, en: t.ok });
        }),
    },
  ]);
}

function concert(): void {
  const t = tx().concert;
  fact('ck_concert', '🎻', t.text, t.phrase, [
    {
      text: t.listen,
      run: () => {
        sfx('fanfare');
        burst('note', 8);
        if (onceToday('ck.concert')) apply({ mood: 8, minutes: 10 });
        say({ who: objName('ck_concert'), icon: '🎶', text: t.heard });
      },
    },
  ]);
}

function koi(): void {
  const t = tx().koi;
  fact('ck_koi', '🐟', t.text, undefined, [
    {
      text: t.feed,
      run: () => {
        if (S().daily['ck.koiFed']) return say({ who: objName('ck_koi'), icon: '🐟', text: t.already });
        if (S().coins < 5) return toast(tx().poor);
        S().daily['ck.koiFed'] = true;
        apply({ coins: -5, mood: 8, minutes: 10 });
        sfx('water');
        burst('heart', 8);
        sayEnglish(t.en, 0.9);
        say({ who: objName('ck_koi'), icon: '🐟', text: `${t.fed}\n\n🗣️ “${t.en}”`, en: t.en });
      },
    },
  ]);
}

function yunhan(): void {
  const t = tx().yunhan;
  sayEnglish(t.en, 0.9);
  ask('ck_yunhan', '🐢', `${t.text}\n\n🗣️ “${t.en}”`, t);
}

function taichi(): void {
  const t = tx().taichi;
  const on = S().time >= 6 * 60 && S().time < 10 * 60;
  if (!on) return fact('ck_taichi', '🧘', t.later);
  fact('ck_taichi', '🧘', t.text, undefined, [
    {
      text: t.join,
      cost: m().common.minutes(30),
      run: () => {
        if (!onceToday('ck.taichi')) return say({ who: objName('ck_taichi'), icon: '🧘', text: t.already });
        sayEnglish(t.en, 0.7);
        apply({ energy: 8, mood: 6, minutes: 30 });
        say({ who: npcName('ck_taichi'), icon: '🧘', text: `🗣️ “${t.en}”\n\n${t.done}`, en: t.en });
      },
    },
  ]);
}

function kite(): void {
  const t = tx().kite;
  fact('ck_kite', '🪁', t.text, undefined, [
    {
      text: t.buy,
      cost: m().common.minutes(30),
      run: () => {
        if (S().coins < 15) return toast(tx().poor);
        apply({ coins: -15, mood: 10, energy: -3, minutes: 30 });
        sfx('flap');
        sayEnglish(t.en, 0.9);
        learned('kite', { stars: 1, study: 1 });
        say({ who: npcName('ck_kiteUncle'), icon: '🪁', text: `🗣️ “${t.en}”\n\n${t.done}\n\n${t.words}`, en: t.en });
      },
    },
  ]);
}

function dance(): void {
  const t = tx().dance;
  const on = S().time >= 18 * 60 && S().time < 22 * 60;
  if (!on) return fact('ck_dance', '💃', t.later);
  fact('ck_dance', '💃', t.text, undefined, [
    {
      text: t.join,
      cost: m().common.minutes(20),
      run: () => {
        sayEnglish(t.en, 0.85);
        burst('note', 8);
        if (onceToday('ck.dance')) apply({ mood: 10, energy: -4, minutes: 20 });
        say({ who: npcName('ck_dancer'), icon: '💃', text: `🗣️ “${t.en}”\n\n${t.done}`, en: t.en });
      },
    },
  ]);
}

function pigeons(): void {
  const t = tx().pigeons;
  fact('ck_pigeons', '🕊️', t.text, t.en, [
    {
      text: t.watch,
      run: () => {
        sfx('flap');
        if (onceToday('ck.pigeons')) apply({ mood: 4, minutes: 5 });
        say({ who: objName('ck_pigeons'), icon: '🕊️', text: t.done });
      },
    },
  ]);
}

function corridor(i = 0): void {
  const t = tx().corridor;
  const r = t.qs[i];
  const who = objName('ck_corridor');
  if (!r) {
    if (onceToday('ck.corridor')) apply({ stars: 3, study: 3 });
    celebrate();
    return say({ who, icon: '📋', text: t.done });
  }
  sayEnglish(r.q, 0.85);
  say({
    who,
    icon: '📋',
    text: i === 0 ? `${t.intro}\n\n${r.q}` : r.q,
    choices: shuffle(r.a.map((a, k) => ({ a, ok: k === 0 }))).map((o) => ({
      text: o.a,
      run: () => {
        if (o.ok) {
          sfx('ok');
          toast(t.right);
          corridor(i + 1);
        } else {
          sfx('bad');
          say({ who, icon: '📋', text: t.wrong, choices: [{ text: m().common.back, run: () => corridor(i) }] });
        }
      },
    })),
  });
}

// --- Beitou -------------------------------------------------------------------------------------

function oldStation(): void {
  const t = tx().oldStation;
  sayEnglish(t.en, 0.85);
  ask('ck_oldStation', '🚉', `${t.text}\n\n🗣️ “${t.en}”`, t);
}

function library(): void {
  const t = tx().library;
  fact('ck_library', '📚', t.text, undefined, [
    {
      text: t.read,
      cost: m().common.minutes(30),
      run: () => {
        apply({ minutes: 30, energy: -2 });
        if (onceToday('ck.read')) apply({ study: 6, mood: 4 });
        sfx('rustle');
        say({ who: objName('ck_library'), icon: '📖', text: t.readDone });
      },
    },
    {
      text: t.ask,
      run: () => {
        sayEnglish(t.en, 0.85);
        learned('library', { study: 2 });
        say({ who: npcName('ck_librarian'), icon: '📚', text: `🗣️ “${t.en}”\n\n${t.reply}`, en: t.en });
      },
    },
    { text: t.quiz, run: () => ask('ck_library', '☀️', t.text, t) },
  ]);
}

function museum(): void {
  const t = tx().museum;
  sayEnglish(t.en, 0.85);
  ask('ck_museum', '🏛️', `${t.text}\n\n🗣️ “${t.en}”`, t);
}

function bath(): void {
  const t = tx().bath;
  const who = objName('ck_bath');
  if (S().time < 5 * 60 + 30 || S().time >= 22 * 60) return say({ who, icon: '♨️', text: t.closed });
  fact('ck_bath', '♨️', t.text, t.en, [
    {
      text: t.go,
      cost: m().common.minutes(60),
      run: () => {
        if (S().daily['ck.bath']) return say({ who, icon: '♨️', text: t.already });
        if (S().coins < 40) return toast(tx().poor);
        sayEnglish(t.rule, 0.85);
        say({
          who: npcName('ck_attendant'),
          icon: '🩱',
          text: t.rule,
          choices: shuffle(t.a.map((a, k) => ({ a, ok: k === 0 }))).map((o) => ({
            text: o.a,
            run: () => {
              sayEnglish(o.a, 0.9);
              if (!o.ok) {
                sfx('bad');
                return say({ who: npcName('ck_attendant'), icon: '🩱', text: t.wrong, choices: [{ text: m().common.back, run: bath }] });
              }
              S().daily['ck.bath'] = true;
              sfx('water');
              apply({ coins: -40, energy: 15, mood: 10, minutes: 60 });
              stamp('beitou');
              burst('drop', 8);
              say({ who, icon: '♨️', text: t.done });
            },
          })),
        });
      },
    },
  ]);
}

function footbath(): void {
  const t = tx().footbath;
  const who = objName('ck_footbath');
  fact('ck_footbath', '🦶', t.text, undefined, [
    {
      text: t.soak,
      cost: m().common.minutes(20),
      run: () => {
        if (!onceToday('ck.footbath')) return say({ who, icon: '🦶', text: t.already });
        const line = t.phrases[Math.floor(Math.random() * t.phrases.length)];
        sfx('water');
        sayEnglish(line, 0.85);
        apply({ energy: 10, mood: 8, minutes: 20 });
        stamp('beitou');
        say({ who, icon: '♨️', text: `🗣️ “${line}”\n\n${t.done}`, en: line });
      },
    },
    { text: t.quiz, run: () => ask('ck_footbath', '🌡️', t.text, t) },
  ]);
}

function valley(): void {
  const t = tx().valley;
  stamp('beitou');
  sayEnglish(t.en, 0.85);
  ask('ck_valley', '🌋', `${t.text}\n\n🗣️ “${t.en}”`, t, 'ck_valley', () => toast(t.fact));
}

function plum(): void {
  const t = tx().plum;
  sayEnglish(t.en, 0.85);
  ask('ck_plum', '🌸', `${t.text}\n\n🗣️ “${t.en}”`, t);
}

function stream(): void {
  const t = tx().stream;
  learned('stream', { study: 2 });
  fact('ck_stream', '💨', t.text, t.en);
}

// --- Registration --------------------------------------------------------------------------------

registerObjects({
  ck_mrt: mrtMenu,
  ck_btMrt: mrtMenu,
  ck_gate: gate,
  ck_steps: steps,
  ck_guard: guard,
  ck_hall: hall,
  ck_theater: theater,
  ck_concert: concert,
  ck_koi: koi,
  ck_yunhan: yunhan,
  ck_taichi: taichi,
  ck_kite: kite,
  ck_snack: () => order('ck_snack', '🧇', { ...tx().snack, price: 30 }, { energy: 10, mood: 6 }),
  ck_bubble: () => order('ck_bubble', '🧋', { ...tx().bubble, price: 45 }, { energy: 6, mood: 10 }),
  ck_corridor: () => corridor(0),
  ck_dance: dance,
  ck_pigeons: pigeons,
  ck_oldStation: oldStation,
  ck_library: library,
  ck_museum: museum,
  ck_bath: bath,
  ck_footbath: footbath,
  ck_valley: valley,
  ck_eggs: () => order('ck_eggs', '🥚', { ...tx().eggs, price: 25 }, { energy: 10, mood: 4 }),
  ck_plum: plum,
  ck_stream: stream,
});

/** NPC talk: rotate through each person's lines; English lines get the replay buttons. */
const ICONS: Record<string, string> = {
  ck_guard: '🎖️', ck_guide: '🚩', ck_taichi: '🧘', ck_taichi2: '🧘', ck_kiteUncle: '🪁', ck_dancer: '💃', ck_teacher: '🏫', ck_tourist: '🎒',
  ck_photographer: '📷', ck_koiGrandma: '🐟', ck_student: '📖', ck_volunteer: 'ℹ️', ck_librarian: '📚', ck_attendant: '♨️', ck_eggAuntie: '🥚',
  ck_hikerA: '🥾', ck_hikerB: '🥾', ck_footGrandpa: '🦶', ck_museumGuide: '🏛️', ck_stationVol: '🚉', ck_tourist2: '👘', ck_kid: '🧒',
};

registerTalk((id) => {
  const lines = tx().talk[id];
  if (!lines?.length) return false;
  const k = `ck.talk.${id}`;
  const n = (S().counters[k] ?? 0) % lines.length;
  S().counters[k] = n + 1;
  const l = lines[n];
  if (l.en) sayEnglish(l.en, 0.9);
  // Some people offer what they look after.
  const extra: Choice[] = [];
  if (id === 'ck_eggAuntie') extra.push({ text: objName('ck_eggs'), run: () => order('ck_eggs', '🥚', { ...tx().eggs, price: 25 }, { energy: 10, mood: 4 }) });
  if (id === 'ck_attendant') extra.push({ text: objName('ck_bath'), run: bath });
  if (id === 'ck_kiteUncle') extra.push({ text: objName('ck_kite'), run: kite });
  if (id === 'ck_footGrandpa') extra.push({ text: objName('ck_footbath'), run: footbath });
  if (id === 'ck_guard') extra.push({ text: objName('ck_guard'), run: guard });
  if (l.en && onceToday(`ck.en.${id}`)) apply({ study: 1 }, { float: false });
  say({ who: npcName(id), icon: ICONS[id] ?? '💬', text: l.en ? `${l.text}\n\n🗣️ “${l.en}”` : l.text, en: l.en, choices: [...extra, { text: m().common.back }] });
  return true;
});

// First arrival of the day: a welcome toast (there is no map-change event, so check gently).
if (typeof window !== 'undefined') {
  window.setInterval(() => {
    if (!hasGame()) return;
    const map = S().player.map;
    if (map === 'cks' && onceToday('ck.welcomeCks')) toast(tx().welcomeCks);
    if (map === 'beitou' && onceToday('ck.welcomeBeitou')) toast(tx().welcomeBeitou);
  }, 1500);
}
